import { Button, FormControl, FormLabel, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Stack, Text, useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { checkLicenseIdDuplicate, createLicense, uploadFile } from "../../../../api";
import { colors } from "../../../../styles";
import { getApiErrorCode } from "../../../../utils";
import FileAttach from "../../../Auth/SignUp/FileAttach";
import { getSignUpErrorMessage } from "../../../Auth/SignUp/errors";
import { FieldMessage, underlineInputProps } from "../../../Auth/SignUp/StepLayout";

interface CreateLicenseModalProps {
    isOpen: boolean;
    onClose: () => void;
}

interface ILicenseForm {
    number: string;
}

const licenseIdRegex = /^\d{1,6}$/;

export default function CreateLicenseModal({ isOpen, onClose }: CreateLicenseModalProps) {
    const queryClient = useQueryClient();
    const toast = useToast();
    const [licenseImage, setLicenseImage] = useState<File | null>(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [debouncedLicenseId, setDebouncedLicenseId] = useState("");

    const { register, handleSubmit, watch, reset } = useForm<ILicenseForm>();
    const licenseId = watch("number") ?? "";
    const isLicenseIdFormatValid = licenseIdRegex.test(licenseId);

    useEffect(() => {
        if (isOpen) {
            reset({ number: "" });
            setLicenseImage(null);
            setErrorMessage("");
        }
    }, [isOpen, reset]);

    useEffect(() => {
        const id = setTimeout(() => setDebouncedLicenseId(licenseId), 400);
        return () => clearTimeout(id);
    }, [licenseId]);

    const { isLoading: isCheckingLicenseId, isError: isLicenseIdCheckError, error: licenseIdCheckError } = useQuery({
        queryKey: ["checkLicenseIdDuplicate", debouncedLicenseId],
        queryFn: () => checkLicenseIdDuplicate({ licenseId: debouncedLicenseId }),
        enabled: isOpen && !!debouncedLicenseId && licenseIdRegex.test(debouncedLicenseId),
        retry: false,
    });

    const isLicenseIdDuplicate = isLicenseIdCheckError && getApiErrorCode(licenseIdCheckError) === "LICENSE_ID_ALREADY_USED";
    const isLicenseIdCheckUnavailable = isLicenseIdCheckError && !isLicenseIdDuplicate;

    const checkLicenseIdDuplicateMutation = useMutation({
        mutationFn: checkLicenseIdDuplicate,
        retry: false,
    });
    const uploadFileMutation = useMutation({ mutationFn: uploadFile });
    const createLicenseMutation = useMutation({
        mutationFn: createLicense,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["me"] });
            toast({
                title: "약사 면허가 등록되었습니다.",
                status: "success",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
            onClose();
        },
    });

    const licenseHint = (() => {
        if (!licenseId) return null;
        if (!isLicenseIdFormatValid) {
            return { message: "약사 면허 번호 형식이 올바르지 않습니다.", tone: "error" as const };
        }
        if (isCheckingLicenseId) {
            return { message: "중복 확인 중...", tone: "muted" as const };
        }
        if (isLicenseIdDuplicate) {
            return { message: "이미 사용 중인 약사 면허 번호입니다.", tone: "error" as const };
        }
        if (isLicenseIdCheckUnavailable) {
            return { message: "약사 면허 번호 중복 확인에 실패했습니다.", tone: "error" as const };
        }
        return { message: "사용 가능한 약사 면허 번호입니다.", tone: "success" as const };
    })();

    const onSave = async ({ number }: ILicenseForm) => {
        if (!licenseImage) {
            setErrorMessage("약사 면허증 사진을 첨부해주세요.");
            return;
        }

        setErrorMessage("");

        try {
            await checkLicenseIdDuplicateMutation.mutateAsync({ licenseId: number });
            const image = await uploadFileMutation.mutateAsync({ file: licenseImage });
            await createLicenseMutation.mutateAsync({ image, number });
        } catch (error) {
            setErrorMessage(getSignUpErrorMessage(error, "약사 면허 등록 중 문제가 발생했습니다."));
        }
    };

    return (
        <Modal motionPreset="slideInBottom" isOpen={isOpen} onClose={onClose} size="xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <Text fontSize={20} fontWeight={700} color={colors.black}>약사 면허 등록</Text>
                    <ModalCloseButton />
                </ModalHeader>
                <ModalBody padding={10} as="form" onSubmit={handleSubmit(onSave)}>
                    <FormControl>
                        <FormLabel fontSize="sm" color={colors.blue}>약사 면허 번호</FormLabel>
                        <Input
                            {...register("number", { required: true })}
                            type="text"
                            placeholder="약사 면허 번호"
                            {...underlineInputProps}
                        />
                        <FieldMessage message={licenseHint?.message} tone={licenseHint?.tone} />
                    </FormControl>
                    <FormControl marginTop={10}>
                        <FormLabel fontSize="sm" color={colors.blue} marginBottom={5}>약사 면허증</FormLabel>
                        <FileAttach
                            label="01 | 약사 면허증"
                            inputId="create-license-upload"
                            file={licenseImage}
                            onChange={(file) => {
                                setLicenseImage(file);
                                setErrorMessage("");
                            }}
                        />
                    </FormControl>
                    <FieldMessage message={errorMessage} />
                    <Stack marginTop={8} width="100%">
                        <Button
                            type="submit"
                            isLoading={
                                uploadFileMutation.isPending ||
                                createLicenseMutation.isPending ||
                                checkLicenseIdDuplicateMutation.isPending
                            }
                            isDisabled={
                                !isLicenseIdFormatValid ||
                                isCheckingLicenseId ||
                                isLicenseIdCheckError ||
                                !licenseImage
                            }
                            width="100%"
                            height="52px"
                            backgroundColor={colors.blue}
                            color={colors.white}
                            borderRadius={10}
                        >
                            저장
                        </Button>
                    </Stack>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
}
